/*
 * Fernwood commerce behaviour (ES module, no dependencies beyond Shopify's standard events).
 * Quick add + quick view, dialogs, recommendations, recently viewed, wishlist page,
 * recent searches, grid loading state and form validation.
 * Everything is event-delegated so section re-renders (filters, pagination, cart) need no re-binding.
 * No innerHTML: server HTML arrives through DOMParser from our own Section Rendering responses,
 * everything else is built with DOM methods.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const RECENT_KEY = 'fw:recent';
const SEARCH_KEY = 'fw:recent-searches';
const WISH_KEY = 'fw:wishlist';
const MAX_RECENT = 12;
const MAX_SEARCHES = 6;
const FEEDBACK_MS = 1600;
const CLOSE_MS = 200;
const SVG_NS = 'http://www.w3.org/2000/svg';

/* ---------- small helpers ---------- */
const readList = (key) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value.filter((v) => typeof v === 'string') : [];
  } catch (error) {
    return [];
  }
};
const writeList = (key, list) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch (error) {
    /* storage unavailable: the feature degrades to this page view */
  }
};
const pushUnique = (key, value, max) => {
  const list = [value, ...readList(key).filter((v) => v !== value)].slice(0, max);
  writeList(key, list);
  return list;
};
const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
const withSection = (url, sectionId) => {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('section_id', sectionId);
  return u.toString();
};

/** Tiny element builder: h('p', { class: 'x' }, 'text', child) */
function h(tag, attrs = {}, ...children) {
  const el = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === false || v === null || v === undefined) return;
    el.setAttribute(k, v === true ? '' : String(v));
  });
  children.flat().forEach((c) => el.append(c instanceof Node ? c : document.createTextNode(String(c))));
  return el;
}

function closeIcon() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '20');
  svg.setAttribute('height', '20');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', 'M5 5l14 14M19 5 5 19');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.8');
  svg.append(path);
  return svg;
}

let live = null;
function announce(text) {
  if (!live) {
    live = h('p', { class: 'fw-visually-hidden', role: 'status', 'aria-live': 'polite' });
    document.body.append(live);
  }
  live.textContent = '';
  window.setTimeout(() => {
    live.textContent = text;
  }, 60);
}

/* ---------- add to bag (feeds Horizon's cart drawer, bubble and cart page) ---------- */
/**
 * @param {number|string|Array<{id: number|string, quantity: number}>} variantId - one variant id, or several lines (one request)
 * @param {number} quantity - ignored when variantId is an array
 * @param {Element} source - element the cart event is dispatched from (inside a modal dialog the drawer waits for close)
 * @returns {Promise<{ok: boolean, message?: string}>}
 */
async function addToBag(variantId, quantity, source) {
  const items = Array.isArray(variantId)
    ? variantId.map((line) => ({ id: Number(line.id), quantity: Number(line.quantity) || 1 }))
    : [{ id: Number(variantId), quantity }];
  const totalQuantity = items.reduce((sum, line) => sum + line.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  const refreshCart = async () => {
    const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
    return res.json();
  };

  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json().catch(() => ({}));
    const failed = !res.ok || Boolean(data.status);
    const cart = await refreshCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'fw-quick-add', itemCount: totalQuantity, didError: failed },
    });
    if (failed) {
      const message = data.description || data.message || 'Could not add to bag. Try again.';
      source.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID', detail: { description: message } }));
      return { ok: false, message };
    }
    return { ok: true };
  } catch (error) {
    /* AbortError keeps Horizon's listeners quiet; the user sees our own message below */
    deferred.reject(Object.assign(new Error('Network error during add to bag'), { name: 'AbortError' }));
    source.dispatchEvent(new CartErrorEvent({ error: 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    return { ok: false, message: 'Connection problem. Check your signal and try again.' };
  }
}

/** Button feedback: loading, then success or error, then back to the label. */
async function runButton(button, task) {
  if (button.getAttribute('aria-busy') === 'true') return { ok: false };
  const label = button.querySelector('[data-fw-btn-label], .fw-card__quick-label');
  const original = label ? label.textContent : '';
  button.setAttribute('aria-busy', 'true');
  button.dataset.state = 'loading';
  if (label) label.textContent = 'Adding';
  const result = await task();
  button.dataset.state = result.ok ? 'done' : 'error';
  if (label) label.textContent = result.ok ? 'Added' : 'Try again';
  button.removeAttribute('aria-busy');
  window.setTimeout(() => {
    if (!button.isConnected) return;
    button.dataset.state = '';
    if (label) label.textContent = original;
  }, FEEDBACK_MS);
  return result;
}

/* ---------- dialogs (size guide + quick view share one behaviour) ---------- */
function openDialog(dialog, trigger) {
  if (!dialog || dialog.open) return;
  dialog.fwTrigger = trigger || document.activeElement;
  dialog.classList.remove('is-closing');
  dialog.showModal();
}

function closeDialog(dialog) {
  if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
  const finish = () => {
    dialog.classList.remove('is-closing');
    dialog.close();
  };
  if (reduceMotion.matches) {
    finish();
    return;
  }
  dialog.classList.add('is-closing');
  window.setTimeout(finish, CLOSE_MS);
}

const isFwDialog = (el) => el instanceof HTMLDialogElement && el.classList.contains('fw-dialog');

document.addEventListener(
  'close',
  (event) => {
    if (!isFwDialog(event.target)) return;
    const trigger = event.target.fwTrigger;
    if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus({ preventScroll: true });
  },
  true
);

document.addEventListener(
  'cancel',
  (event) => {
    if (!isFwDialog(event.target)) return;
    event.preventDefault();
    closeDialog(event.target);
  },
  true
);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-fw-dialog-open]');
  if (opener) {
    openDialog(document.getElementById(opener.dataset.fwDialogOpen), opener);
    return;
  }
  const closer = event.target.closest('[data-fw-dialog-close]');
  if (closer) {
    closeDialog(closer.closest('dialog'));
    return;
  }
  /* a click that lands on the dialog element itself is a click on the backdrop */
  if (isFwDialog(event.target)) {
    const r = event.target.getBoundingClientRect();
    const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside) closeDialog(event.target);
  }
});

/* Keep Tab inside an open Fernwood dialog (native modals still let focus reach the browser chrome). */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab') return;
  const dialog = [...document.querySelectorAll('dialog.fw-dialog[open]')].pop();
  if (!dialog) return;
  const items = [...dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el.getClientRects().length);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  const inside = dialog.contains(document.activeElement);
  if (event.shiftKey && (document.activeElement === first || !inside)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
    event.preventDefault();
    first.focus();
  }
});

/* ---------- quick view ---------- */
let qvDialog = null;
function getQuickView() {
  if (qvDialog) return qvDialog;
  const close = h('button', {
    type: 'button',
    class: 'fw-dialog__close fw-dialog__close--float',
    'data-fw-dialog-close': true,
    'aria-label': 'Close quick view',
  });
  close.append(closeIcon());
  qvDialog = h('dialog', { class: 'fw-dialog fw-dialog--qv', 'aria-label': 'Quick view' }, close, h('div', { class: 'fw-dialog__content', 'data-fw-qv-content': true }));
  document.body.append(qvDialog);
  return qvDialog;
}

function qvSkeleton() {
  return h(
    'div',
    { class: 'fw-qv fw-qv--loading', 'aria-busy': 'true' },
    h('div', { class: 'fw-qv__gallery' }, h('span', { class: 'fw-skel__img' })),
    h(
      'div',
      { class: 'fw-qv__info' },
      h('span', { class: 'fw-skel__line' }),
      h('span', { class: 'fw-skel__line fw-skel__line--short' }),
      h('span', { class: 'fw-skel__block' })
    ),
    h('p', { class: 'fw-visually-hidden' }, 'Loading product')
  );
}

async function openQuickView(url, trigger, options = {}) {
  const dialog = getQuickView();
  const content = dialog.querySelector('[data-fw-qv-content]');
  content.replaceChildren(qvSkeleton());
  dialog.setAttribute('aria-label', 'Quick view');
  dialog.removeAttribute('aria-labelledby');
  dialog.fwMoveFromWishlist = options.moveFromWishlist || null;
  openDialog(dialog, trigger);
  try {
    const res = await fetch(withSection(url, 'fw-quick-view'), { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Quick view ${res.status}`);
    const qv = parseHTML(await res.text()).querySelector('[data-fw-qv]');
    if (!qv) throw new Error('Quick view markup missing');
    content.replaceChildren(document.adoptNode(qv));
    dialog.setAttribute('aria-labelledby', qv.getAttribute('aria-labelledby'));
    dialog.removeAttribute('aria-label');
    initQuickView(qv, dialog);
    const first = qv.querySelector('.fw-opt__input:checked') || qv.querySelector('[data-fw-qv-add]');
    first?.focus({ preventScroll: true });
  } catch (error) {
    content.replaceChildren(
      h('div', { class: 'fw-qv__fail' }, h('p', { class: 'fw-body' }, "This product didn't load."), h('a', { class: 'fw-btn', href: url }, 'View details'))
    );
  }
}

function renderPrice(el, variant) {
  const parts = variant.compare
    ? [
        h('span', { class: 'fw-visually-hidden' }, 'Sale price'),
        h('span', {}, variant.price),
        h('span', { class: 'fw-visually-hidden' }, 'Regular price'),
        h('s', { class: 'fw-card__compare' }, variant.compare),
      ]
    : [h('span', {}, variant.price)];
  el.replaceChildren(...parts);
}

function initQuickView(qv, dialog) {
  let variants = [];
  try {
    variants = JSON.parse(qv.querySelector('[data-fw-qv-variants]').textContent);
  } catch (error) {
    variants = [];
  }
  const form = qv.querySelector('[data-fw-qv-form]');
  const idInput = qv.querySelector('[data-fw-qv-id]');
  const addBtn = qv.querySelector('[data-fw-qv-add]');
  const addLabel = addBtn.querySelector('[data-fw-btn-label]');
  const priceEl = qv.querySelector('[data-fw-qv-price]');
  const stockEl = qv.querySelector('[data-fw-qv-stock]');
  const errorEl = qv.querySelector('[data-fw-qv-error]');
  const fieldsets = [...qv.querySelectorAll('[data-fw-opt-index]')];
  const track = qv.querySelector('.fw-qv__track');
  const indexEl = qv.querySelector('[data-fw-qv-index]');

  const selected = () => fieldsets.map((fs) => fs.querySelector('.fw-opt__input:checked')?.value);
  const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

  function markAvailability(current) {
    fieldsets.forEach((fs, index) => {
      fs.querySelectorAll('.fw-opt__input').forEach((input) => {
        const probe = [...current];
        probe[index] = input.value;
        const match = findVariant(probe);
        const soldOut = !match || !match.available;
        const label = qv.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        label?.classList.toggle('is-sold-out', soldOut);
        input.setAttribute('aria-disabled', String(soldOut));
        const status = label?.querySelector('[data-fw-opt-status]');
        if (status) status.textContent = soldOut ? ', sold out' : '';
      });
      const chosen = fs.querySelector('[data-fw-opt-chosen]');
      if (chosen) chosen.textContent = `: ${current[index] ?? ''}`;
    });
  }

  function update() {
    const current = selected();
    const variant = findVariant(current);
    errorEl.hidden = true;
    markAvailability(current);
    if (!variant) {
      idInput.value = '';
      addBtn.disabled = true;
      addLabel.textContent = 'Unavailable';
      stockEl.textContent = '';
      return;
    }
    idInput.value = variant.id;
    addBtn.disabled = !variant.available;
    addLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
    renderPrice(priceEl, variant);
    stockEl.textContent = variant.available && variant.tracked && variant.qty > 0 && variant.qty <= 5 ? `Only ${variant.qty} left` : '';
    if (variant.media && track) {
      const slide = track.querySelector(`[data-media-id="${variant.media}"]`);
      if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  }

  form.addEventListener('change', update);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!idInput.value || addBtn.disabled) return;
    const result = await runButton(addBtn, () => addToBag(idInput.value, 1, form));
    if (!result.ok) {
      errorEl.textContent = result.message || 'Could not add to bag. Try again.';
      errorEl.hidden = false;
      return;
    }
    announce('Added to bag');
    const handle = dialog.fwMoveFromWishlist;
    if (handle && window.fwWishlist) window.fwWishlist.set(handle, false);
    window.setTimeout(() => closeDialog(dialog), 450);
  });

  if (track && indexEl) {
    track.addEventListener(
      'scroll',
      () => {
        indexEl.textContent = String(Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)) + 1);
      },
      { passive: true }
    );
  }
  update();
}

/* ---------- card quick add / quick view (delegated) ---------- */
document.addEventListener('click', async (event) => {
  const add = event.target.closest('[data-fw-quick-add]');
  if (add) {
    event.preventDefault();
    const result = await runButton(add, () => addToBag(add.dataset.fwQuickAdd, 1, add));
    announce(result.ok ? 'Added to bag' : result.message || 'Could not add to bag');
    const item = add.closest('[data-fw-wish-item]');
    if (result.ok && item && window.fwWishlist) window.fwWishlist.set(item.dataset.fwWishItem, false);
    return;
  }
  const view = event.target.closest('[data-fw-quick-view]');
  if (view) {
    event.preventDefault();
    const item = view.closest('[data-fw-wish-item]');
    openQuickView(view.dataset.fwQuickView, view, { moveFromWishlist: item ? item.dataset.fwWishItem : null });
  }
});

/* ---------- recommendations (Complete the look, You may also like, Similar pieces) ---------- */
async function loadRecs(host) {
  const body = host.querySelector('[data-fw-recs-body]');
  try {
    const res = await fetch(host.dataset.url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Recommendations ${res.status}`);
    const list = parseHTML(await res.text()).querySelector('.fw-grid-list');
    if (!list || !list.children.length) {
      host.remove();
      return;
    }
    body.replaceChildren(document.adoptNode(list));
    body.removeAttribute('aria-busy');
    host.hidden = false;
  } catch (error) {
    host.remove();
  }
}

/* ---------- card lists fetched by handle (recently viewed, wishlist) ---------- */
async function fetchCard(handle) {
  const res = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=fw-card-render`, { credentials: 'same-origin' });
  if (res.status === 404) return { handle, missing: true };
  if (!res.ok) throw new Error(`Card ${res.status}`);
  const card = parseHTML(await res.text()).querySelector('.fw-card:not(.fw-card--empty)');
  return { handle, card: card ? document.adoptNode(card) : null, missing: !card };
}

const listOf = (items) => h('ul', { class: 'fw-grid-list', role: 'list' }, items);

async function loadRecent(host) {
  const current = host.dataset.current;
  if (current) pushUnique(RECENT_KEY, current, MAX_RECENT);
  const limit = Number(host.dataset.limit) || 4;
  const handles = readList(RECENT_KEY).filter((h2) => h2 !== current).slice(0, limit);
  if (!handles.length) {
    host.remove();
    return;
  }
  const results = await Promise.allSettled(handles.map(fetchCard));
  const found = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
  const gone = found.filter((r) => r.missing).map((r) => r.handle);
  if (gone.length) writeList(RECENT_KEY, readList(RECENT_KEY).filter((x) => !gone.includes(x)));
  const cards = found.filter((r) => r.card).map((r) => h('li', {}, r.card));
  if (!cards.length) {
    host.remove();
    return;
  }
  const body = host.querySelector('[data-fw-recs-body]');
  body.replaceChildren(listOf(cards));
  body.removeAttribute('aria-busy');
  host.hidden = false;
}

/* ---------- wishlist page ---------- */
function wishlistActions(handle, card) {
  const bar = h('div', { class: 'fw-wish-actions' });
  const quick = card.querySelector('[data-fw-quick-add], [data-fw-quick-view]');
  const title = card.querySelector('.fw-card__title')?.textContent.trim() || 'item';
  if (quick) {
    const move = quick.cloneNode(false);
    move.className = 'fw-btn fw-wish-actions__move';
    move.setAttribute('aria-label', `Move ${title} to bag`);
    move.replaceChildren(h('span', { 'data-fw-btn-label': true }, 'Move to bag'));
    bar.append(move);
    quick.remove();
  } else {
    bar.append(h('p', { class: 'fw-mono fw-muted fw-wish-actions__sold' }, 'Sold out'));
  }
  bar.append(
    h('button', { type: 'button', class: 'fw-btn fw-btn--ghost fw-wish-actions__remove', 'data-fw-wish-remove': handle, 'aria-label': `Remove ${title} from wishlist` }, 'Remove')
  );
  return bar;
}

function setWishCount(host, n) {
  host.querySelector('[data-fw-wishlist-count]').textContent = n ? `${n} saved` : '';
}

function showWishEmpty(host, empty) {
  host.querySelector('[data-fw-wishlist-loading]').hidden = true;
  host.querySelector('[data-fw-wishlist-grid]').hidden = empty;
  host.querySelector('[data-fw-wishlist-empty]').hidden = !empty;
}

async function renderWishlist(host) {
  const grid = host.querySelector('[data-fw-wishlist-grid]');
  const handles = window.fwWishlist ? window.fwWishlist.list() : readList(WISH_KEY);
  if (!handles.length) {
    showWishEmpty(host, true);
    setWishCount(host, 0);
    return;
  }
  const results = await Promise.allSettled(handles.map(fetchCard));
  const items = [];
  results.forEach((r) => {
    if (r.status !== 'fulfilled') return;
    const { handle, card, missing } = r.value;
    if (missing) {
      window.fwWishlist?.set(handle, false);
      return;
    }
    items.push(h('li', { 'data-fw-wish-item': handle }, card, wishlistActions(handle, card)));
  });
  grid.replaceChildren(...items);
  showWishEmpty(host, items.length === 0);
  setWishCount(host, items.length);
}

function initWishlistPage(host) {
  renderWishlist(host);
  host.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-fw-wish-remove]');
    if (remove) window.fwWishlist?.set(remove.dataset.fwWishRemove, false);
  });
  document.addEventListener('fw:wishlist-change', (event) => {
    if (event.detail.saved) return;
    const item = host.querySelector(`[data-fw-wish-item="${CSS.escape(event.detail.handle)}"]`);
    if (!item) return;
    const next = item.nextElementSibling || item.previousElementSibling;
    const drop = () => {
      item.remove();
      const left = host.querySelectorAll('[data-fw-wish-item]').length;
      setWishCount(host, left);
      if (!left) showWishEmpty(host, true);
      (next?.querySelector('.fw-card__link') || host.querySelector('h1'))?.focus({ preventScroll: true });
    };
    if (reduceMotion.matches) {
      drop();
      return;
    }
    item.classList.add('is-leaving');
    window.setTimeout(drop, CLOSE_MS);
  });
}

/* ---------- recent searches ---------- */
function renderRecentSearches() {
  const terms = readList(SEARCH_KEY);
  document.querySelectorAll('[data-fw-recent-searches]').forEach((box) => {
    const chips = terms.map((term) =>
      h('li', {}, h('a', { class: 'fw-chip', href: `${root}search?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last` }, term))
    );
    box.querySelector('[data-fw-recent-list]').replaceChildren(...chips);
    const input = box.closest('form')?.querySelector('input[type="search"]');
    box.hidden = terms.length === 0 || Boolean(input && input.value.trim());
  });
}

function recordSearch(term) {
  const clean = String(term || '').trim().replace(/\s+/g, ' ').slice(0, 60);
  if (clean.length < 2) return;
  pushUnique(SEARCH_KEY, clean, MAX_SEARCHES);
}

document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-fw-recent-clear]')) return;
  writeList(SEARCH_KEY, []);
  renderRecentSearches();
  announce('Recent searches cleared');
});
/* Searches are recorded on the results page, and only when they found something. */
document.addEventListener('input', (event) => {
  if (event.target.matches?.('input[type="search"]')) renderRecentSearches();
});

/* ---------- grid loading state while filters and sort re-render ---------- */
function trackGridUpdate(event) {
  const promise = event.promise || event.detail?.promise;
  const html = document.documentElement;
  html.classList.add('fw-grid-loading');
  const done = () => html.classList.remove('fw-grid-loading');
  if (promise && typeof promise.finally === 'function') promise.finally(done).catch(() => {});
  else window.setTimeout(done, 1500);
}
document.addEventListener(StandardEvents.collectionUpdate, trackGridUpdate);
document.addEventListener(StandardEvents.searchUpdate, trackGridUpdate);

/* ---------- forms: inline validation (labels above, errors below) ---------- */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('fw-form')) return;
  let firstBad = null;
  form.querySelectorAll('[required]').forEach((field) => {
    const value = field.value.trim();
    const bad = !value || (field.type === 'email' && !EMAIL_RE.test(value));
    const error = document.getElementById(field.getAttribute('aria-describedby'));
    if (bad) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
    if (error) error.hidden = !bad;
    if (bad && !firstBad) firstBad = field;
  });
  if (firstBad) {
    event.preventDefault();
    firstBad.focus();
  }
});

/* ---------- mobile sort: proxy to the filter drawer's own sort control ---------- */
/* Horizon builds the results URL from the drawer's facets form, so changing the drawer's control
   (not a second form) keeps every active filter. */
document.addEventListener('change', (event) => {
  const proxy = event.target;
  if (!(proxy instanceof HTMLSelectElement) || !proxy.matches('[data-fw-mobile-sort]')) return;
  const scope = document.getElementById('filters-drawer') || proxy.closest('.shopify-section') || document;
  // Only controls that belong to the drawer's facets form count: Horizon also renders an overflow
  // sort whose radios point at another form via the form attribute.
  const form = scope.querySelector('form[id^="FacetFiltersForm"]');
  if (!form) return;
  const value = proxy.value;
  const controls = [...form.elements].filter((el) => el.name === 'sort_by');
  const radio = controls.find((el) => el instanceof HTMLInputElement && el.value === value);
  const select = controls.find((el) => el instanceof HTMLSelectElement);
  const target = radio || select;
  if (!target) return;
  if (radio) radio.checked = true;
  if (select) {
    select.disabled = false;
    select.value = value;
  }
  target.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ---------- cart add-on rail (free-shipping nudge in the drawer and on the cart page) ---------- */
/* The rail is Liquid (snippets/fw-free-ship.liquid) and re-renders with the cart. When it has no
   collection candidates it carries data-fw-addons-url and data-skip-subtree-update: the rows then come
   from Shopify recommendations, fetched here and kept across morphs until the next fetch replaces them.
   Adding moves focus to the row now in the same slot, or to the shipping status once the rail is gone. */
const ADDON_FOCUS_MS = 30000;
let addonFocus = null;

const addonHosts = () => [...document.querySelectorAll('[data-fw-freeship]')];
const isShown = (el) => el.getClientRects().length > 0;

document.addEventListener(
  'click',
  (event) => {
    const btn = event.target.closest('[data-fw-addon-btn]');
    const host = btn?.closest('[data-fw-freeship]');
    if (!host) return;
    const index = [...host.querySelectorAll('[data-fw-addon-btn]')].indexOf(btn);
    addonFocus = { scope: host.dataset.fwFreeship, index, until: Date.now() + ADDON_FOCUS_MS };
  },
  true
);

/* Any other user interaction (qty stepper, remove, a link) cancels the pending focus move.
   Programmatic focus from the drawer or quick view does not. */
const cancelAddonFocus = (event) => {
  if (!addonFocus) return;
  if (event.target.closest?.('[data-fw-addon-btn], dialog.fw-dialog')) return;
  addonFocus = null;
};
document.addEventListener('pointerdown', cancelAddonFocus, true);
document.addEventListener('keydown', cancelAddonFocus, true);

function applyAddonFocus() {
  if (!addonFocus) return;
  if (Date.now() > addonFocus.until) {
    addonFocus = null;
    return;
  }
  /* quick view still open: wait for it to close (its close handler returns focus first) */
  if (document.querySelector('dialog.fw-dialog[open]')) return;
  const host = addonHosts().find((el) => el.dataset.fwFreeship === addonFocus.scope && isShown(el));
  if (!host) return;
  if (host.fwAddonsPending) return;
  const buttons = [...host.querySelectorAll('[data-fw-addon-btn]')].filter(isShown);
  const target = buttons[Math.min(addonFocus.index, buttons.length - 1)] || host.querySelector('[data-fw-freeship-status]');
  addonFocus = null;
  target?.focus();
}

async function loadAddonRows(rail, key) {
  const host = rail.closest('[data-fw-freeship]');
  const urls = [rail.dataset.fwAddonsUrl, rail.dataset.fwAddonsFallback].filter(Boolean);
  if (host) host.fwAddonsPending = true;
  /* both sources in parallel; the first non-empty one in order wins */
  const lists = await Promise.all(
    urls.map(async (url) => {
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return [];
        const list = parseHTML(await res.text()).querySelector('.fw-addons__list');
        return list ? [...list.children] : [];
      } catch (error) {
        return []; /* network hiccup: the other source may still answer; an empty rail stays hidden */
      }
    })
  );
  const rows = lists.find((list) => list.length) || [];
  if (!rail.isConnected || rail.dataset.fwAddonsKey !== key) return; /* a newer cart took over */
  rail.querySelector('.fw-addons__list')?.replaceChildren(...rows.map((row) => document.adoptNode(row)));
  if (host) host.fwAddonsPending = false;
  applyAddonFocus();
}

function syncAddons() {
  addonHosts().forEach((host) => {
    const key = host.dataset.fwCartKey;
    if (host.fwSeenKey === key) return;
    host.fwSeenKey = key;
    const rail = host.querySelector('[data-fw-addons-url]');
    if (rail) {
      /* drop rows for products that are in the bag now, then refetch for the new total */
      const inBag = new Set((key.split('_')[0] || '').split('-'));
      rail.querySelectorAll('[data-fw-addon-product]').forEach((row) => {
        if (inBag.has(row.dataset.fwAddonProduct)) row.remove();
      });
      loadAddonRows(rail, rail.dataset.fwAddonsKey);
      return;
    }
    host.fwAddonsPending = false;
  });
  applyAddonFocus();
}

let addonFrame = 0;
const scheduleAddons = () => {
  if (addonFrame) return;
  addonFrame = window.requestAnimationFrame(() => {
    addonFrame = 0;
    syncAddons();
  });
};
new MutationObserver(scheduleAddons).observe(document.body, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['data-fw-cart-key'],
});
document.addEventListener('close', () => window.requestAnimationFrame(applyAddonFocus), true);
window.setTimeout(syncAddons, 0);

/* ---------- build your fit (build C): pick one per slot, add all in one request (delegated) ---------- */
const buildMoney = (form, cents) => {
  const currency = form.closest('[data-fw-build]')?.dataset.currency || 'USD';
  try {
    return (cents / 100).toLocaleString(document.documentElement.lang || 'en', { style: 'currency', currency });
  } catch (error) {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

function buildSlotVariants(slot) {
  const input = slot.querySelector('.fw-build__input:checked');
  if (!input) return [];
  try {
    const list = JSON.parse(input.dataset.variants || '[]');
    return Array.isArray(list) ? list.filter((v) => v && Number.isFinite(Number(v.id))) : [];
  } catch (error) {
    return [];
  }
}

/** Rebuild the size picker for the chosen product (DOM methods only). */
function refreshBuildSlot(slot) {
  const select = slot.querySelector('[data-fw-build-size]');
  const wrap = select?.closest('.fw-build__size');
  const input = slot.querySelector('.fw-build__input:checked');
  if (!select || !input) return;
  const variants = buildSlotVariants(slot);
  const keep = select.value;
  select.replaceChildren(
    ...variants.map((v) => {
      const option = document.createElement('option');
      option.value = String(v.id);
      option.dataset.price = String(v.price);
      option.textContent = v.title;
      return option;
    })
  );
  if (variants.some((v) => String(v.id) === keep)) select.value = keep;
  if (wrap) wrap.hidden = input.dataset.single === 'true' || variants.length < 2;
}

function refreshBuildTotal(form) {
  let cents = 0;
  form.querySelectorAll('[data-fw-build-size]').forEach((select) => {
    cents += Number(select.selectedOptions[0]?.dataset.price || 0);
  });
  const out = form.querySelector('[data-fw-build-total]');
  if (out) out.textContent = buildMoney(form, cents);
}

document.addEventListener('change', (event) => {
  const form = event.target.closest?.('[data-fw-build-form]');
  if (!form) return;
  if (event.target.matches('.fw-build__input')) refreshBuildSlot(event.target.closest('[data-fw-build-slot]'));
  refreshBuildTotal(form);
  const status = form.querySelector('[data-fw-build-status]');
  if (status) status.textContent = '';
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('[data-fw-build-form]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('[data-fw-build-add]');
  const status = form.querySelector('[data-fw-build-status]');
  const slots = [...form.querySelectorAll('[data-fw-build-slot]')];
  const lines = slots.map((slot) => ({ id: slot.querySelector('[data-fw-build-size]')?.value, quantity: 1 }));
  const missing = slots.findIndex((slot, i) => !lines[i].id);
  if (missing > -1) {
    if (status) status.textContent = 'Pick an option in every step first.';
    slots[missing].querySelector('.fw-build__input')?.focus();
    return;
  }
  const result = await runButton(button, () => addToBag(lines, lines.length, button));
  const message = result.ok ? `Added ${lines.length} pieces to your bag.` : result.message || 'Could not add to bag. Try again.';
  if (status) status.textContent = message;
});

/* ---------- init ---------- */
/* Hosts start hidden (nothing to show until Shopify answers), so load them when the browser is idle
   rather than on intersection: a hidden element never intersects. */
const whenIdle = (fn) => ('requestIdleCallback' in window ? window.requestIdleCallback(fn, { timeout: 1500 }) : window.setTimeout(fn, 200));

function init(scope = document) {
  scope.querySelectorAll('[data-fw-recs][data-url]').forEach((host) => whenIdle(() => loadRecs(host)));
  scope.querySelectorAll('[data-fw-recent]').forEach(loadRecent);
  scope.querySelectorAll('[data-fw-wishlist]').forEach(initWishlistPage);

  const searchHead = scope.querySelector('[data-fw-search-terms]');
  if (searchHead?.dataset.fwSearchTerms) recordSearch(searchHead.dataset.fwSearchTerms);
  renderRecentSearches();

  scope.querySelector('[data-fw-focus]')?.focus();
}

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
window.fwCommerce = { addToBag, openQuickView };
